import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { MenuSectionDTO } from '@lib/strapi/dtos/components/sections/menuSection.dto';
import { dataOf } from '../shared-ui';
import PhoneLink from '@components/utils/phone-link';
import { IconCalendar, IconCheck, IconClock, IconPin, IconStar } from '@components/utils/icons';
import { GoogleReviewsData } from '@lib/frontend/google-reviews';
import { FooterProps } from '@lib/models/layout/footer';
import { directionsUrl, fullAddress } from '@lib/utils/geo';
import { hoursLabel } from '@lib/utils/hours';
import { openAppointmentModal } from '../shared-ui';
import * as gtag from '@lib/utilities/analytics';

/** deterministic anchor for a heading; the ELOT transliteration is not needed
 *  here — encodeURIComponent-safe latin/greek slug is enough for a fragment */
const anchorOf = (text: string, i: number) =>
  `s-${i}-${text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '-').replace(/^-|-$/g, '').slice(0, 60)}`;

/**
 * «Σε αυτή τη σελίδα» — built client-side from the page's own `article h2`
 * headings (the Q&A questions on treatment/condition bodies). This is the
 * artifact-v5 sticky TOC, and it is deliberately NOT a model field: the
 * headings already exist in the rich text, and a CMS-maintained copy of them
 * would drift the first time an editor rephrases a question.
 *
 * The effect also ASSIGNS the ids (rich text from Strapi has none), so the
 * links have something to point at, and tracks the ACTIVE heading on scroll
 * (the artifact's `.toc li.on` state) — the last heading above the reading
 * line wins.
 */
const useArticleToc = () => {
  const { asPath } = useRouter();
  const [toc, setToc] = useState<{ id: string; text: string }[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    const heads = Array.from(document.querySelectorAll<HTMLHeadingElement>('article h2'));
    setToc(
      heads.map((h, i) => {
        // legal chapters number themselves («1. Υπεύθυνος…») — the TOC has
        // its own numerals, so the text drops the duplicate prefix
        const text = (h.textContent?.trim() ?? '').replace(/^\d+\.\s*/, '');
        if (!h.id) h.id = anchorOf(text, i);
        // anchor lands the heading below the sticky header, not underneath it
        h.style.scrollMarginTop = '90px';
        return { id: h.id, text };
      }).filter((t) => t.text)
    );

    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const line = 140; // just under the sticky header
        let current: string | null = null;
        for (const h of heads) {
          if (h.getBoundingClientRect().top <= line) current = h.id;
          else break;
        }
        setActiveId(current);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [asPath]);

  return { toc, activeId };
};

/**
 * The aside column (D34), in the artifact's order: the in-page TOC, then the
 * BOOKING CARD directly under it (owner, 2026-09-07 — the conversion card
 * must not sink below the link list), then the seeded «Δείτε Επίσης» menu.
 * hideOnMobile defaults true — on a phone this duplicates the burger menu
 * and the headings are one flick away anyway.
 */
const MenuSectionImplementation = ({ section, page }: ComponentProps<MenuSectionDTO>) => {
  const { toc, activeId } = useArticleToc();
  const { t, lang } = useTranslation('main');
  const tocLabel = t('toc.onThisPage');
  if (!section) return null;
  const menus = dataOf(page).sidebarMenus as
    | Record<string, { items?: { text?: string; url?: string; subMenu?: { items?: { text?: string; url?: string }[] } }[] }>
    | undefined;
  const menu = menus?.[section.menu];
  const groups = menu?.items ?? [];
  const reviews = dataOf(page).googleReviews as GoogleReviewsData | null | undefined;
  const footer = (page as { footer?: FooterProps } | undefined)?.footer;
  const address = fullAddress({
    addressStreet: footer?.addressStreet,
    addressLocality: footer?.addressLocality,
    addressPostalCode: footer?.addressPostalCode,
  });

  return (
    <div className={`flex flex-col gap-4 ${section.hideOnMobile ? 'hidden lg:flex' : ''}`}>
      {toc.length > 1 ? (
        <nav className="rounded-2xl border border-rule bg-white p-5" aria-label={tocLabel}>
          <p className="mb-3 text-xs font-extrabold tracking-wide text-muted">{tocLabel}</p>
          <ol className="flex list-none flex-col gap-2.5">
            {toc.map((item, i) => {
              const on = activeId === item.id;
              return (
                <li key={item.id} className="relative flex gap-2 text-sm leading-snug">
                  {/* the artifact's .toc li.on: accent + 800 + a 3px bar in
                      the card's padding gutter */}
                  {on ? (
                    <span aria-hidden className="absolute -left-5 bottom-0.5 top-0.5 w-[3px] rounded bg-violet-700" />
                  ) : null}
                  <span aria-hidden
                    className={`font-extrabold tabular-nums ${on ? 'text-violet-700' : 'text-violet-400'}`}>
                    {i + 1}
                  </span>
                  <a href={`#${item.id}`} aria-current={on ? 'location' : undefined}
                    className={`transition-colors hover:text-violet-700 hover:underline ${
                      on ? 'font-extrabold text-violet-700' : 'font-bold text-body'
                    }`}>
                    {item.text}
                  </a>
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}
      {/* the artifact's .bookcard, in its exact order: check-marked kicker,
          the NUMBER as the card's headline, clock-marked hours, then the
          full-width «Κλείστε ραντεβού» button (owner, 2026-09-07 — both
          conversion paths belong on the card, call AND book) */}
      <div className="flex flex-col gap-2 rounded-2xl border border-violet-200 bg-violet-050 p-5">
        <p className="inline-flex items-center gap-1.5 text-xs font-extrabold tracking-wide text-violet-700">
          <IconCheck width={15} height={15} strokeWidth={2.4} aria-hidden />
          {t('common.freeAssessment')}
        </p>
        <PhoneLink variant="card" placement="sidebar" />
        <p className="inline-flex items-center gap-1.5 text-sm text-muted">
          <IconClock width={14} height={14} aria-hidden />
          {/* structured CMS hours (P8p); the locale key is the fallback for
              a site-settings record without them */}
          {hoursLabel(footer, lang) ?? t('common.hours')}
        </p>
        {/* address in the same info register — entity pages carry no map
            section, so this is their only "where" (owner, 2026-09-08);
            links straight to directions-from-here (D48 pattern) */}
        {address ? (
          <a href={directionsUrl(address)} target="_blank" rel="noreferrer"
            onClick={() => gtag.event('map_directions_click', { event_category: 'engagement', event_label: 'sidebar' })}
            className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-violet-700 hover:underline">
            <IconPin width={14} height={14} className="flex-none" aria-hidden />
            {address}
          </a>
        ) : null}
        {/* hairline splits INFO (what/when/where) from PROOF + ACTION — the
            card was reading as one undifferentiated list (owner, 2026-09-08) */}
        <hr className="my-1 border-violet-200" aria-hidden />
        {/* live Google aggregate, flat on the card — one line, stars + bold
            figure + count; absent when the Places fetch is (D21) */}
        {reviews ? (
          <a href={reviews.profileUrl} target="_blank" rel="noreferrer"
            onClick={() => gtag.event('review_click', { event_category: 'engagement', event_label: 'sidebar' })}
            className="group/agg flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="inline-flex items-center gap-0.5" aria-hidden>
              {[1, 2, 3, 4, 5].map((i) => (
                <IconStar key={i} width={16} height={16}
                  className={i <= Math.round(reviews.rating) ? 'text-arc-yellow' : 'text-rule'} />
              ))}
            </span>
            <span className="text-lg font-extrabold leading-none tabular-nums text-ink">
              {reviews.rating.toLocaleString('el', { minimumFractionDigits: 1 })}
            </span>
            <span className="w-full text-sm font-bold text-muted transition-colors group-hover/agg:text-violet-700">
              {t('reviews.countLabel', { count: reviews.count })}
            </span>
          </a>
        ) : null}
        {/* opens the popup (D47): interactive CTAs stay in context — the
            /kleiste-rantevou PAGE remains the direct-pointer door (owner,
            2026-09-08) */}
        <button type="button" onClick={() => openAppointmentModal('sidebar')}
          className="mt-0.5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-violet-700 px-4 py-2.5 text-sm font-extrabold text-white transition-colors hover:bg-violet-800">
          <IconCalendar width={16} height={16} aria-hidden />
          {t('cta.bookAppointment')}
        </button>
      </div>
      {groups.length ? (
        <nav className="rounded-2xl border border-rule bg-white p-5" aria-label={section.heading ?? t('menuSection.navAria')}>
          {section.heading ? (
            <p className="mb-3 text-xs font-extrabold tracking-wide text-muted">{section.heading}</p>
          ) : null}
          <ul className="flex flex-col">
            {/* chevron rows with a visible hover surface — plain links gave
                no affordance that these are the exits (owner, 2026-09-08) */}
            {groups.flatMap((g) => g.subMenu?.items ?? [g]).map((item, i) => (
              <li key={i}>
                <Link href={item.url ?? '#'}
                  className="group/lnk -mx-2 flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm font-bold text-violet-700 transition-colors hover:bg-violet-050">
                  {item.text}
                  <span aria-hidden
                    className="text-violet-400 transition-transform group-hover/lnk:translate-x-0.5 group-hover/lnk:text-violet-700">
                    ›
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}
    </div>
  );
};

export default MenuSectionImplementation;
